import Text from '@/components/ui/custom/dataDisplay/Text';
import { t } from 'i18next';
import moment from 'moment';
import { Fragment, useMemo } from 'react';
import Countdown from 'react-countdown';
import { useTranslation } from 'react-i18next';
import { getCurrentStage, getNextStage } from '@/utils/hackathon';
import CountdownItem from '@/components/ui/custom/CountdownItem';
import useHackathon from '@/data/hook/hackathon/useHackathon';

export default function HackathonCountdown() {
  const { refetchStages, hackathonsStages } = useHackathon();
  const { t } = useTranslation('translation', {
    keyPrefix: 'hackathon',
  });

  const { stage, isCurrent, date } = useMemo(() => {
    const currentStage = getCurrentStage(hackathonsStages);
    const nextStage = getNextStage(hackathonsStages);
    const activeStage = currentStage || nextStage;

    if (!activeStage) return { stage: null, isCurrent: false, date: null };

    const dateRaw = currentStage?.endDate || nextStage?.startDate;
    return {
      stage: activeStage,
      isCurrent: !!currentStage,
      date: moment(dateRaw),
    };
  }, [hackathonsStages]);

  if (!stage) return <Fragment />;

  return (
    <div className="flex items-center flex-col gap-1.5">
      <div className="relative flex gap-1">
        <Text text={t('stages.' + stage.type)} className="leading-7 font-600" />
        <Text
          text={isCurrent ? t('countdown.finishAt') : t('countdown.startAt')}
          className="relative leading-7"
        />
      </div>
      <Countdown
        key={stage.id}
        date={date.valueOf()}
        renderer={CountdownRenderer}
        onComplete={() => {
          refetchStages();
        }}
      />
    </div>
  );
}

type CountdownRendererProps = {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
};

export const CountdownRenderer = ({
  days,
  hours,
  minutes,
  seconds,
}: CountdownRendererProps) => {
  return (
    <div className="grid grid-cols-4 justify-between text-base-100 gap-2">
      <CountdownItem
        label={t('countdown.days')}
        value={days}
        classNames="bg-hackathon-primary"
      />
      <CountdownItem
        label={t('countdown.hours')}
        value={hours}
        classNames="bg-hackathon-primary"
      />
      <CountdownItem
        label={t('countdown.minutes')}
        value={minutes}
        classNames="bg-hackathon-primary"
      />
      <CountdownItem
        label={t('countdown.seconds')}
        value={seconds}
        classNames="bg-hackathon-primary"
      />
    </div>
  );
};
